/**
 * Feiertagskalender des Mandanten (AP-22-B).
 *
 * Der Kalender ist Stammdatum, nicht Berechnung: `feiertageAT` aus
 * `@symphenia/payroll-core` belegt ein Jahr vor, danach gehört er dem Mandanten.
 * Deshalb überschreibt „Jahr vorbelegen" nichts — es ergänzt nur, was fehlt.
 *
 * AP-36: Zeilenblock wie die Fehlzeit-Typen. Jede Zeile ist eine Speicherstelle
 * (`holiday:<id>`) und speichert beim Verlassen; die leere letzte Zeile legt an,
 * sobald Datum und Bezeichnung dastehen. „Jahr vorbelegen" ist eine Handlung und
 * behält ihren Knopf.
 */

import { useCallback, useEffect, useState } from 'react';
import { CalendarPlus, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import { pageWide } from '@/lib/form-styles';
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock';
import {
  FeldStatusZeichen,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import { createHoliday, deleteHoliday, fetchHolidays, generateHolidays } from '../api/payroll.api';
import { formatIsoDate, weekdayOf } from '../lib/dates';
import type { Holiday } from '../types/payroll.types';

const SPALTEN: SpaltenDef[] = [
  { key: 'date', label: 'Datum', spur: '9.5rem', min: 140, schmal: 'w-40' },
  { key: 'weekday', label: 'Wochentag', spur: '7rem', min: 90, schmal: 'w-28' },
  { key: 'name', label: 'Bezeichnung', spur: 'minmax(12rem,3fr)', min: 180, schmal: 'w-full' },
  { key: 'statutory', label: 'Gesetzlich', spur: '5.5rem', min: 70, schmal: 'w-20' },
  { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
];

export default function HolidaysPage() {
  const authUser = useAuthStore((s) => s.user);
  // Pflegen ist ein eigenes Recht (AP-30): Wer nur liest, sieht die Felder gesperrt.
  const darfPflegen = erfuelltRecht('payroll.holiday.manage', authUser?.permissions);

  const [jahr, setJahr] = useState(() => new Date().getFullYear());
  const [rows, setRows] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const fetchRows = useCallback(async (j: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHolidays({ year: j, limit: 100 });
      setRows(res.data);
    } catch (err) {
      console.error('Failed to load holidays', err);
      setError('Die Feiertage konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setNotice(null);
    void fetchRows(jahr);
  }, [jahr, fetchRows]);

  const vorbelegen = async () => {
    setNotice(null);
    setError(null);
    try {
      const res = await generateHolidays(jahr);
      setNotice(
        res.created > 0
          ? `${res.created} Feiertage für ${jahr} ergänzt${res.skipped > 0 ? `, ${res.skipped} waren schon da` : ''}.`
          : `Für ${jahr} war bereits alles hinterlegt — nichts geändert.`,
      );
      await fetchRows(jahr);
    } catch (err) {
      console.error('Failed to generate holidays', err);
      setError('Die Vorbelegung ist fehlgeschlagen.');
    }
  };

  const loeschen = async (h: Holiday) => {
    if (
      !(await bestaetige(`„${h.name}" am ${formatIsoDate(h.date)} löschen?`, {
        ok: 'Löschen',
        gefaehrlich: true,
      }))
    )
      return;
    try {
      await deleteHoliday(h.id);
      await fetchRows(jahr);
    } catch (err) {
      console.error('Failed to delete holiday', err);
      setError('Löschen fehlgeschlagen.');
    }
  };

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Feiertage</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Grundlage für Feiertagszuschläge und Sollzeiten. Eine Zeile wird gespeichert, sobald du
            sie verlässt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-border">
            <button
              type="button"
              className="rounded-l-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setJahr((j) => j - 1)}
              aria-label="Vorjahr"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="min-w-[4rem] text-center text-sm font-medium tabular-nums">{jahr}</span>
            <button
              type="button"
              className="rounded-r-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => setJahr((j) => j + 1)}
              aria-label="Folgejahr"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          {darfPflegen && (
            <Button variant="outline" size="sm" onClick={() => void vorbelegen()} disabled={loading}>
              <CalendarPlus className="h-4 w-4" />
              <span className="hidden sm:inline">{jahr} vorbelegen</span>
            </Button>
          )}
        </div>
      </div>

      {notice && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          {notice}
        </div>
      )}
      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      <ZeilenListe spalten={SPALTEN}>
        {loading && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">Laden…</p>
        )}
        {!loading && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Für {jahr} ist noch nichts hinterlegt.
            {darfPflegen && ` „${jahr} vorbelegen" legt die gesetzlichen Feiertage an.`}
          </p>
        )}
        {rows.map((h) => (
          <FeiertagZeile
            key={h.id}
            feiertag={h}
            gesperrt={!darfPflegen}
            onLoeschen={() => void loeschen(h)}
          />
        ))}
        {darfPflegen && (
          <NeuerFeiertag jahr={jahr} onAngelegt={() => void fetchRows(jahr)} onFehler={setError} />
        )}
      </ZeilenListe>
    </div>
  );
}

function FeiertagZeile({
  feiertag,
  gesperrt,
  onLoeschen,
}: {
  feiertag: Holiday;
  gesperrt: boolean;
  onLoeschen: () => void;
}) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `holiday:${feiertag.id}`,
    bezeichnung: feiertag.name,
    daten: feiertag as unknown as Datensatz,
    schreibe: (patch, stand) =>
      api
        .patch<Datensatz>(`/payroll/holidays/${feiertag.id}`, patch, mitStand(stand))
        .then((r) => r.data),
    keepalive: { url: `/payroll/holidays/${feiertag.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;
  const gruppe = useGruppe(stelle, { label: feiertag.name });
  const datum = useFeld<string | null>(stelle, 'date', {
    gruppe,
    art: 'datum',
    pflicht: true,
    label: 'Datum',
    gesperrt,
  });
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, label: 'Bezeichnung', gesperrt });
  const gesetzlich = useFeld<boolean>(stelle, 'statutory', {
    gruppe,
    art: 'schalter',
    label: 'Gesetzlich',
    gesperrt,
  });

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={feiertag.id}>
      <Spalte spalte="date">
        <input type="date" aria-label="Datum" className={`${zelle} tabular-nums`} data-start {...datum.props} />
      </Spalte>
      <Spalte spalte="weekday">
        <span className="px-1.5 text-sm text-muted-foreground">
          {datum.wert ? weekdayOf(datum.wert) : ''}
        </span>
      </Spalte>
      <Spalte spalte="name">
        <input aria-label="Bezeichnung" className={zelle} {...name.props} />
      </Spalte>
      <Spalte spalte="statutory">
        <span
          className="flex items-center justify-center gap-1"
          title="Ohne Haken: betrieblich ergänzt, z. B. Betriebsurlaub"
        >
          <input
            type="checkbox"
            aria-label="Gesetzlich"
            className="h-4 w-4 accent-[hsl(var(--primary))]"
            {...gesetzlich.schalter}
          />
          <FeldStatusZeichen status={gesetzlich} />
        </span>
      </Spalte>
      <Spalte spalte="aktion">
        {!gesperrt && (
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            onClick={onLoeschen}
            title="Löschen"
            aria-label="Löschen"
          >
            <Trash2 size={14} />
          </button>
        )}
      </Spalte>
    </ZeilenBlock>
  );
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald Datum und Bezeichnung dastehen. */
function NeuerFeiertag({
  jahr,
  onAngelegt,
  onFehler,
}: {
  jahr: number;
  onAngelegt: () => void;
  onFehler: (t: string | null) => void;
}) {
  const leer = { date: '', name: '', statutory: true };
  const [neu, setNeu] = useState(leer);

  const anlegen = async () => {
    if (!neu.date || !neu.name.trim()) return;
    try {
      onFehler(null);
      await createHoliday({ date: neu.date, name: neu.name.trim(), statutory: neu.statutory });
      setNeu(leer);
      onAngelegt();
    } catch (err) {
      const code = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      onFehler(
        code === 'payroll.holidays.duplicateDate'
          ? 'Für dieses Datum ist bereits ein Feiertag hinterlegt.'
          : 'Feiertag konnte nicht angelegt werden.',
      );
    }
  };

  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="date">
        <input
          type="date"
          aria-label="Datum des neuen Feiertags"
          className={`${zelle} tabular-nums`}
          min={`${jahr}-01-01`}
          max={`${jahr}-12-31`}
          value={neu.date}
          onChange={(e) => setNeu({ ...neu, date: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="weekday" leer>
        <span className="px-1.5 text-sm text-muted-foreground">
          {neu.date ? weekdayOf(neu.date) : ''}
        </span>
      </Spalte>
      <Spalte spalte="name">
        <input
          aria-label="Bezeichnung des neuen Feiertags"
          className={zelle}
          placeholder="Neuer Feiertag …"
          value={neu.name}
          onChange={(e) => setNeu({ ...neu, name: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="statutory" leer>
        <span className="flex items-center justify-center">
          <input
            type="checkbox"
            aria-label="Gesetzlich"
            className="h-4 w-4 accent-[hsl(var(--primary))]"
            checked={neu.statutory}
            onChange={(e) => setNeu({ ...neu, statutory: e.target.checked })}
          />
        </span>
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  );
}
